import React from 'react';
import { CardData } from '../types';
import { VsmCanvasShape } from './VsmCanvasShapes';
import { useTheme } from '../context/ThemeContext';
import { useProjectStore } from '../store/useProjectStore';
import { useSimulationSafe } from '../context/SimulationContext';

interface InventoryTriangleProps {
  data: CardData;
  selected: boolean;
  cardWidth: number;
  cardHeight: number;
  onUpdateCardData?: (id: string, data: Partial<CardData>) => void;
  handlePointerDown: (e: React.PointerEvent) => void;
  handlePointerMove: (e: React.PointerEvent) => void;
  handlePointerUp: (e: React.PointerEvent) => void;
}

const VSM_INVENTORY_ICONS = new Set(['supermercado', 'fifo', 'emergencia', 'estoque', 'pulmao']);
const BLOCK_ICON_RADIUS = 19.2;
const BLOCK_ICON_SLASH_X = 14.6;
const BLOCK_ICON_SLASH_Y = 11.2;
const BLOCK_ICON_FILL = 'rgba(255,255,255,0.72)';
const BLOCK_ICON_STROKE = 'rgba(220,38,38,0.9)';
const BLOCK_ICON_SLASH = 'rgba(220,38,38,0.95)';

const toFactoryMinutes = (value: unknown, unit: unknown, fallbackUnit: 's' | 'min' | 'h' = 's') => {
  const numericValue = Number(value) || 0;
  const resolvedUnit = typeof unit === 'string' && unit.length > 0 ? unit : fallbackUnit;
  if (['dias', 'dia', 'd'].includes(resolvedUnit)) return numericValue * 1440;
  if (['horas', 'hora', 'h'].includes(resolvedUnit)) return numericValue * 60;
  if (['minutos', 'minuto', 'min'].includes(resolvedUnit)) return numericValue;
  return numericValue / 60;
};

const formatWipValue = (value: number) => {
  if (value >= 1000) return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  if (value >= 100) return Math.round(value).toString();
  if (value >= 10) return value.toFixed(0);
  return value.toFixed(1).replace('.', ',');
};

export const InventoryTriangle: React.FC<InventoryTriangleProps> = ({
  data,
  selected,
  cardWidth,
  cardHeight,
  onUpdateCardData,
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
}) => {
  const inventoryValue = data.inventory || 0;
  const vsmIcon = data.vsmIcon;
  const [overlayDrag, setOverlayDrag] = React.useState<{
    pointerId: number;
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [overlayLiveOffset, setOverlayLiveOffset] = React.useState<{ x: number; y: number } | null>(null);
  const overlayOffsetRef = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const { isDark } = useTheme();
  const simulation = useSimulationSafe();
  const viewMode = useProjectStore(state => state.viewMode);
  const cards = useProjectStore(state => {
    const activeSheet = state.sheets.find(s => s.id === state.activeSheetId);
    return activeSheet?.cards ?? [];
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    if (onUpdateCardData) {
      onUpdateCardData(data.id, { inventory: value });
    }
  };

  // VSM variant: supermercado, fifo, emergencia, estoque, pulmao
  if (vsmIcon && VSM_INVENTORY_ICONS.has(vsmIcon)) {
    const shapeW = cardWidth;
    const shapeH = cardHeight;
    const inventory = (data as any).inventory;
    const vsmUnit = (data as any).vsmUnit;
    const vsmDias = (data as any).vsmDias;
    const isSupermarket = vsmIcon === 'supermercado';
    const isCurrentClean = viewMode === 'current_clean';
    const isFutureMode = viewMode === 'future_simulated';
    const isFlowCellBlocked = !isCurrentClean && Boolean((data as any).vsmFlowCellBlocked) && vsmIcon === 'estoque';
    const isConvertedSupermarket = !isCurrentClean && ((vsmIcon === 'estoque' && (data as any).vsmConvertedToIcon === 'supermercado') || (isSupermarket && (data as any).vsmConvertedFromIcon === 'estoque'));
    const isConvertedPulmao = !isCurrentClean && vsmIcon === 'estoque' && (data as any).vsmConvertedToIcon === 'pulmao';
    // V208.1: in future mode, explicitly blocked stocks (safety net — should be caught by futHidden first)
    if (isFutureMode && isFlowCellBlocked) return null;
    const inventoryMode = isCurrentClean && vsmIcon === 'estoque' && (data as any).vsmConvertedToIcon === 'supermercado'
      ? undefined
      : (data as any).inventoryMode;
    const showCoverage = isSupermarket || inventoryMode === 'coverage' || (vsmDias != null && !inventory);
    const variants: { label: string; qty: number }[] = ((data as any).inventoryVariants ?? []).filter((v: any) => v.label || v.qty > 0);
    const inventoryValueColor = isDark ? '#e2e8f0' : '#111827';
    const simIsActive = Boolean(simulation?.isActive ?? simulation?.isSimulationMode);
    const nextProcess = cards
      .filter(card => card.tipo === 'atividade' && (card as any).vsmIcon === 'processo' && card.posicao.x >= data.posicao.x)
      .sort((a, b) => a.posicao.x - b.posicao.x)[0];
    const shiftMinutes = simulation?.shiftPreset === '2_shifts' ? 960 : simulation?.shiftPreset === 'plus_2h' ? 600 : 480;
    const nextOverride = nextProcess ? simulation?.kaizenOverrides?.[nextProcess.id] : undefined;
    const nextCycle = nextProcess ? toFactoryMinutes((nextProcess as any).vsmCT ?? nextProcess.tp, (nextProcess as any).vsmCTUnit, 's') : 0;
    const nextOperators = Math.max(1, Number((nextProcess as any)?.vsmOp ?? nextProcess?.pessoasSimultaneas ?? 1));
    const extraOperators = Math.max(0, Number(nextOverride?.fte ?? nextOverride?.extraFTE ?? 0));
    const ctReduction = Math.max(0, Math.min(100, Number(nextOverride?.rework ?? nextOverride?.reworkReduction ?? 0)));
    const adjustedCycle = nextCycle * (1 - ctReduction / 100);
    const capacityPerDay = adjustedCycle > 0 ? (shiftMinutes * (nextOperators + extraOperators)) / adjustedCycle : 0;
    const simDemand = Math.max(0, Number(simulation?.demand ?? simulation?.simulatedDemand ?? 0));
    const dailyAccumulation = simIsActive && simDemand > 0 && capacityPerDay > 0 ? Math.max(0, simDemand - capacityPerDay) : 0;
    const simulatedInventory = Math.max(0, Number(inventory) || 0) + dailyAccumulation;
    const fixedBufferDays = Math.max(0, Number((data as any).vsmFixedLeadTimeDays ?? (data as any).vsmBufferDays ?? vsmDias ?? 1));
    const displayedInventory = isSupermarket ? fixedBufferDays : simIsActive ? simulatedInventory : Number(inventory) || 0;
    const bottleneckRatio = Math.max(
      capacityPerDay > 0 && simDemand > 0 ? simDemand / capacityPerDay : 0,
      Number(inventory) > 0 ? simulatedInventory / Math.max(Number(inventory), 1) : dailyAccumulation > 0 ? 1.2 : 0,
    );
    const isInventoryBottleneck = !isSupermarket && simIsActive && bottleneckRatio > 1;
    const glowAlpha = Math.min(0.95, 0.35 + (bottleneckRatio - 1) * 0.45);
    const glowSize = Math.min(34, 14 + (bottleneckRatio - 1) * 18);
    const displayText = formatWipValue(displayedInventory);
    const dynamicFontSize = Math.max(10, Math.min(18, 42 / Math.max(displayText.length, 2)));
    if (isConvertedSupermarket) {
      const stockW = shapeW;
      const stockH = shapeH;
      const marketW = Math.round(shapeW * 0.782);
      const marketH = Math.round(shapeH * 0.833);

      // V208.1: future mode — render clean supermercado (no ghost estoque, no cancel slash)
      // V210: centralizar supermercado no bbox do card para que handles/click area fiquem alinhados
      // (no Atual/Kaizen o overlay fica deslocado para cima — comportamento legítimo de "conversão visual";
      // no Futuro o card É um supermercado, então deve ocupar o bbox normalmente).
      if (isFutureMode) {
        const marketLeft = Math.round((shapeW - marketW) / 2);
        const marketTop = Math.round((shapeH - marketH) / 2);
        return (
          <div
            className="relative"
            style={{ width: `${shapeW}px`, height: `${shapeH}px`, cursor: 'move', overflow: 'visible' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            <div aria-hidden="true" style={{ position: 'absolute', inset: -8, zIndex: 0, background: 'rgba(0,0,0,0.001)', pointerEvents: 'auto' }} />
            <div style={{ position: 'absolute', left: marketLeft, top: marketTop, width: marketW, height: marketH, pointerEvents: 'auto' }}>
              <VsmCanvasShape vsmIcon="supermercado" width={marketW} height={marketH} selected={selected} />
              {fixedBufferDays > 0 && (
                <div style={{ position: 'absolute', top: marketH + 4, left: 0, width: '100%', textAlign: 'center', fontFamily: "'JetBrains Mono','Courier New',monospace", fontSize: 10, fontWeight: 700, color: isDark ? '#22d3ee' : '#0e7490', pointerEvents: 'none', whiteSpace: 'nowrap', lineHeight: 1 }}>
                  {`${formatWipValue(fixedBufferDays)} ${(vsmUnit && !['pcs','pc','un'].includes(String(vsmUnit).toLowerCase())) ? vsmUnit : 'dias'}`}
                </div>
              )}
            </div>
          </div>
        );
      }

      const defaultMarketLeft = Math.round(shapeW * 0.46);
      const defaultMarketTop = -Math.round(shapeH * 1.08);
      const persistedOffset = (data as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 };
      const overlayOffset = overlayDrag
        ? { x: overlayDrag.offsetX, y: overlayDrag.offsetY }
        : overlayLiveOffset ?? { x: Number(persistedOffset.x) || 0, y: Number(persistedOffset.y) || 0 };
      const marketLeft = defaultMarketLeft + overlayOffset.x;
      const marketTop = defaultMarketTop + overlayOffset.y;
      const handleOverlayPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        overlayOffsetRef.current = overlayOffset;
        setOverlayDrag({
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          baseX: overlayOffset.x,
          baseY: overlayOffset.y,
          offsetX: overlayOffset.x,
          offsetY: overlayOffset.y,
        });
      };
      const handleOverlayPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!overlayDrag || overlayDrag.pointerId !== event.pointerId) return;
        event.stopPropagation();
        const nextOffset = {
          x: Math.round(overlayDrag.baseX + event.clientX - overlayDrag.startX),
          y: Math.round(overlayDrag.baseY + event.clientY - overlayDrag.startY),
        };
        overlayOffsetRef.current = nextOffset;
        setOverlayLiveOffset(nextOffset);
        setOverlayDrag({ ...overlayDrag, offsetX: nextOffset.x, offsetY: nextOffset.y });
        onUpdateCardData?.(data.id, { vsmSupermarketOverlayOffset: nextOffset } as any);
      };
      const handleOverlayPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!overlayDrag || overlayDrag.pointerId !== event.pointerId) return;
        event.stopPropagation();
        event.currentTarget.releasePointerCapture(event.pointerId);
        const nextOffset = overlayOffsetRef.current;
        setOverlayLiveOffset(nextOffset);
        onUpdateCardData?.(data.id, { vsmSupermarketOverlayOffset: nextOffset } as any);
        setOverlayDrag(null);
      };

      return (
        <div
          className="flex flex-col items-center justify-center relative"
          style={{ width: `${shapeW}px`, height: `${shapeH}px`, cursor: 'move', overflow: 'visible' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: -8,
              zIndex: 0,
              background: 'rgba(0,0,0,0.001)',
              pointerEvents: 'auto',
            }}
          />

          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: stockW,
              height: stockH,
              opacity: 0.45,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <VsmCanvasShape vsmIcon="estoque" width={stockW} height={stockH} selected={false} />
            {(() => {
              const total = variants.length > 0 ? variants.reduce((s, v) => s + (Number(v.qty) || 0), 0) : Number(inventory) || 0;
              return total > 0 ? (
                <div style={{ position: 'absolute', top: '59%', left: 0, right: 0, transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', gap: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: inventoryValueColor, lineHeight: 1 }}>{total.toLocaleString('pt-BR')}</span>
                  {vsmUnit && <span style={{ fontSize: 9, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', lineHeight: 1 }}>{vsmUnit}</span>}
                </div>
              ) : null;
            })()}
            {variants.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: shapeW, fontFamily: 'Arial, sans-serif', pointerEvents: 'none' }}>
                {variants.map((v, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap', lineHeight: 1.05 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase' }}>{v.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827' }}>{v.qty > 0 ? v.qty.toLocaleString('pt-BR') : '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <svg
            width={stockW}
            height={stockH}
            viewBox={`0 0 ${stockW} ${stockH}`}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 2,
              overflow: 'visible',
              pointerEvents: 'none',
            }}
          >
            <circle cx={stockW / 2} cy={stockH / 2} r={BLOCK_ICON_RADIUS} fill="none" stroke={BLOCK_ICON_STROKE} strokeWidth={1.7} vectorEffect="non-scaling-stroke" />
            <line x1={stockW / 2 - BLOCK_ICON_SLASH_X} y1={stockH / 2 + BLOCK_ICON_SLASH_Y} x2={stockW / 2 + BLOCK_ICON_SLASH_X} y2={stockH / 2 - BLOCK_ICON_SLASH_Y} stroke={BLOCK_ICON_SLASH} strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>

          <div
            style={{
              position: 'absolute',
              left: marketLeft,
              top: marketTop,
              width: marketW,
              height: marketH,
              zIndex: 3,
              filter: 'drop-shadow(0 0 8px rgba(249,115,22,0.28))',
              pointerEvents: 'auto',
              cursor: overlayDrag ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
            onPointerDown={handleOverlayPointerDown}
            onPointerMove={handleOverlayPointerMove}
            onPointerUp={handleOverlayPointerUp}
            onPointerCancel={handleOverlayPointerUp}
          >
            <VsmCanvasShape vsmIcon="supermercado" width={marketW} height={marketH} selected={selected} />
            {fixedBufferDays > 0 && (
              <div style={{
                position: 'absolute',
                top: -18,
                left: 0,
                width: '100%',
                textAlign: 'center',
                fontFamily: "'JetBrains Mono','Courier New',monospace",
                fontSize: 10,
                fontWeight: 700,
                color: isDark ? '#22d3ee' : '#0e7490',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}>
                {`${formatWipValue(fixedBufferDays)} ${(vsmUnit && !['pcs','pc','un'].includes(String(vsmUnit).toLowerCase())) ? vsmUnit : 'dias'}`}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (isConvertedPulmao) {
      const stockW = shapeW;
      const stockH = shapeH;
      const pulmaoW = Math.round(shapeW * 0.553);
      const pulmaoH = Math.round(shapeH * 0.935);

      // V208.1: future mode — render clean pulmão (no ghost estoque, no cancel slash)
      // V210: centralizar pulmão no bbox do card no Futuro (alinha handles com o ícone visível).
      if (isFutureMode) {
        const pulmaoLeft = Math.round((shapeW - pulmaoW) / 2);
        const pulmaoTop = Math.round((shapeH - pulmaoH) / 2);
        return (
          <div
            className="relative"
            style={{ width: `${shapeW}px`, height: `${shapeH}px`, cursor: 'move', overflow: 'visible' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            <div aria-hidden="true" style={{ position: 'absolute', inset: -8, zIndex: 0, background: 'rgba(0,0,0,0.001)', pointerEvents: 'auto' }} />
            <div style={{ position: 'absolute', left: pulmaoLeft, top: pulmaoTop, width: pulmaoW, height: pulmaoH, pointerEvents: 'auto' }}>
              <VsmCanvasShape vsmIcon="pulmao" width={pulmaoW} height={pulmaoH} selected={selected} />
              {fixedBufferDays > 0 && (
                <div style={{ position: 'absolute', top: pulmaoH + 4, left: 0, width: '100%', textAlign: 'center', fontFamily: "'JetBrains Mono','Courier New',monospace", fontSize: 10, fontWeight: 700, color: isDark ? '#22d3ee' : '#0e7490', pointerEvents: 'none', whiteSpace: 'nowrap', lineHeight: 1 }}>
                  {`${formatWipValue(fixedBufferDays)} ${(vsmUnit && !['pcs','pc','un'].includes(String(vsmUnit).toLowerCase())) ? vsmUnit : 'dias'}`}
                </div>
              )}
            </div>
          </div>
        );
      }

      const defaultPulmaoLeft = Math.round(shapeW * 0.46);
      const defaultPulmaoTop  = -Math.round(shapeH * 1.15);
      const persistedOffset = (data as any).vsmPulmaoOverlayOffset ?? { x: 0, y: 0 };
      const overlayOffset = overlayDrag
        ? { x: overlayDrag.offsetX, y: overlayDrag.offsetY }
        : overlayLiveOffset ?? { x: Number(persistedOffset.x) || 0, y: Number(persistedOffset.y) || 0 };
      const pulmaoLeft = defaultPulmaoLeft + overlayOffset.x;
      const pulmaoTop  = defaultPulmaoTop  + overlayOffset.y;

      const handleOverlayPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        overlayOffsetRef.current = overlayOffset;
        setOverlayDrag({
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          baseX: overlayOffset.x,
          baseY: overlayOffset.y,
          offsetX: overlayOffset.x,
          offsetY: overlayOffset.y,
        });
      };
      const handleOverlayPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!overlayDrag || overlayDrag.pointerId !== event.pointerId) return;
        event.stopPropagation();
        const nextOffset = {
          x: Math.round(overlayDrag.baseX + event.clientX - overlayDrag.startX),
          y: Math.round(overlayDrag.baseY + event.clientY - overlayDrag.startY),
        };
        overlayOffsetRef.current = nextOffset;
        setOverlayLiveOffset(nextOffset);
        setOverlayDrag({ ...overlayDrag, offsetX: nextOffset.x, offsetY: nextOffset.y });
        onUpdateCardData?.(data.id, { vsmPulmaoOverlayOffset: nextOffset } as any);
      };
      const handleOverlayPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!overlayDrag || overlayDrag.pointerId !== event.pointerId) return;
        event.stopPropagation();
        event.currentTarget.releasePointerCapture(event.pointerId);
        const nextOffset = overlayOffsetRef.current;
        setOverlayLiveOffset(nextOffset);
        onUpdateCardData?.(data.id, { vsmPulmaoOverlayOffset: nextOffset } as any);
        setOverlayDrag(null);
      };

      return (
        <div
          className="flex flex-col items-center justify-center relative"
          style={{ width: `${shapeW}px`, height: `${shapeH}px`, cursor: 'move', overflow: 'visible' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div aria-hidden="true" style={{ position: 'absolute', inset: -8, zIndex: 0, background: 'rgba(0,0,0,0.001)', pointerEvents: 'auto' }} />

          {/* Ghost estoque */}
          <div style={{ position: 'absolute', left: 0, top: 0, width: stockW, height: stockH, opacity: 0.45, zIndex: 1, pointerEvents: 'none' }}>
            <VsmCanvasShape vsmIcon="estoque" width={stockW} height={stockH} selected={false} />
            {(() => {
              const total = variants.length > 0 ? variants.reduce((s, v) => s + (Number(v.qty) || 0), 0) : Number(inventory) || 0;
              return total > 0 ? (
                <div style={{ position: 'absolute', top: '59%', left: 0, right: 0, transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', gap: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: inventoryValueColor, lineHeight: 1 }}>{total.toLocaleString('pt-BR')}</span>
                  {vsmUnit && <span style={{ fontSize: 9, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', lineHeight: 1 }}>{vsmUnit}</span>}
                </div>
              ) : null;
            })()}
            {variants.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: shapeW, fontFamily: 'Arial, sans-serif', pointerEvents: 'none' }}>
                {variants.map((v, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap', lineHeight: 1.05 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase' }}>{v.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827' }}>{v.qty > 0 ? v.qty.toLocaleString('pt-BR') : '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Block icon (cancelado) */}
          <svg width={stockW} height={stockH} viewBox={`0 0 ${stockW} ${stockH}`} style={{ position: 'absolute', left: 0, top: 0, zIndex: 2, overflow: 'visible', pointerEvents: 'none' }}>
            <circle cx={stockW / 2} cy={stockH / 2} r={BLOCK_ICON_RADIUS} fill="none" stroke={BLOCK_ICON_STROKE} strokeWidth={1.7} vectorEffect="non-scaling-stroke" />
            <line x1={stockW / 2 - BLOCK_ICON_SLASH_X} y1={stockH / 2 + BLOCK_ICON_SLASH_Y} x2={stockW / 2 + BLOCK_ICON_SLASH_X} y2={stockH / 2 - BLOCK_ICON_SLASH_Y} stroke={BLOCK_ICON_SLASH} strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>

          {/* Pulmão overlay — draggable */}
          <div
            style={{ position: 'absolute', left: pulmaoLeft, top: pulmaoTop, width: pulmaoW, height: pulmaoH, zIndex: 3, filter: 'drop-shadow(0 0 8px rgba(147,51,234,0.28))', pointerEvents: 'auto', cursor: overlayDrag ? 'grabbing' : 'grab', touchAction: 'none' }}
            onPointerDown={handleOverlayPointerDown}
            onPointerMove={handleOverlayPointerMove}
            onPointerUp={handleOverlayPointerUp}
            onPointerCancel={handleOverlayPointerUp}
          >
            <VsmCanvasShape vsmIcon="pulmao" width={pulmaoW} height={pulmaoH} selected={selected} />
            {fixedBufferDays > 0 && (
              <div style={{ position: 'absolute', top: -18, left: 0, width: '100%', textAlign: 'center', fontFamily: "'JetBrains Mono','Courier New',monospace", fontSize: 10, fontWeight: 700, color: isDark ? '#22d3ee' : '#0e7490', pointerEvents: 'none', whiteSpace: 'nowrap', lineHeight: 1 }}>
                {`${formatWipValue(fixedBufferDays)} ${(vsmUnit && !['pcs','pc','un'].includes(String(vsmUnit).toLowerCase())) ? vsmUnit : 'dias'}`}
              </div>
            )}
          </div>


        </div>
      );
    }

    return (
      <div
        className="flex flex-col items-center justify-center relative"
        style={{ width: `${shapeW}px`, height: `${shapeH}px`, cursor: 'move' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: 'rgba(0,0,0,0.001)',
            pointerEvents: 'auto',
          }}
        />
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: `${shapeW}px`,
            height: `${shapeH}px`,
            filter: isInventoryBottleneck ? `drop-shadow(0 0 ${glowSize}px rgba(255,0,0,${glowAlpha}))` : undefined,
            animation: isInventoryBottleneck && bottleneckRatio > 1.35 ? 'vsmBottleneckPulse 900ms ease-in-out infinite alternate' : undefined,
            pointerEvents: 'none',
          }}
        >
          <VsmCanvasShape vsmIcon={vsmIcon} width={shapeW} height={shapeH} selected={selected} />
          {isFlowCellBlocked && (
            <svg
              width={shapeW}
              height={shapeH}
              viewBox={`0 0 ${shapeW} ${shapeH}`}
              style={{ position: 'absolute', left: 0, top: 0, zIndex: 3, overflow: 'visible', pointerEvents: 'none' }}
            >
              <circle cx={shapeW / 2} cy={shapeH / 2} r={BLOCK_ICON_RADIUS} fill="none" stroke={BLOCK_ICON_STROKE} strokeWidth={1.7} vectorEffect="non-scaling-stroke" />
              <line x1={shapeW / 2 - BLOCK_ICON_SLASH_X} y1={shapeH / 2 + BLOCK_ICON_SLASH_Y} x2={shapeW / 2 + BLOCK_ICON_SLASH_X} y2={shapeH / 2 - BLOCK_ICON_SLASH_Y} stroke={BLOCK_ICON_SLASH} strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
          )}
          {/* Inventory qty + unit overlaid inside the shape */}
          {(() => {
            // For blocked estoque: total = sum of variants (or inventory field). Always show.
            const blockedTotal = isFlowCellBlocked
              ? (variants.length > 0 ? variants.reduce((s, v) => s + (Number(v.qty) || 0), 0) : Number(inventory) || 0)
              : 0;
            const blockedTotalText = blockedTotal > 0 ? blockedTotal.toLocaleString('pt-BR') : null;
            return (
              <div style={{ position: 'absolute', top: '59%', left: 0, right: 0, transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none', gap: 1 }}>
                {isFlowCellBlocked ? (
                  <>
                    {blockedTotalText && <span style={{ fontSize: 11, fontWeight: 800, color: inventoryValueColor, lineHeight: 1 }}>{blockedTotalText}</span>}
                    {vsmUnit && <span style={{ fontSize: 9, fontWeight: 600, color: isDark ? '#94a3b8' : '#6b7280', lineHeight: 1 }}>{vsmUnit}</span>}
                  </>
                ) : isSupermarket ? (
              <>
                <span style={{ fontSize: dynamicFontSize, fontWeight: 800, color: inventoryValueColor, lineHeight: 1, maxWidth: shapeW * 0.58, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
                <span style={{ fontSize: 8, color: '#444', lineHeight: 1 }}>Buffer d</span>
              </>
            ) : simIsActive ? (
              <>
                <span style={{ fontSize: dynamicFontSize, fontWeight: 800, color: isInventoryBottleneck ? '#dc2626' : inventoryValueColor, lineHeight: 1, maxWidth: shapeW * 0.58, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
                <span style={{ fontSize: 8, color: isInventoryBottleneck ? '#dc2626' : '#444', lineHeight: 1 }}>WIP</span>
              </>
            ) : showCoverage ? (
              <>
                <span style={{ fontSize: 11, fontWeight: 700, color: inventoryValueColor, lineHeight: 1 }}>{vsmDias}</span>
                <span style={{ fontSize: 9, color: '#444', lineHeight: 1 }}>Dias</span>
              </>
            ) : (
              <>
                {inventory ? <span style={{ fontSize: 11, fontWeight: 700, color: inventoryValueColor, lineHeight: 1 }}>{inventory}</span> : null}
                {vsmUnit ? <span style={{ fontSize: 9, color: '#444', lineHeight: 1 }}>{vsmUnit}</span> : null}
              </>
              )}
              </div>
            );
          })()}
          {/* Variantes abaixo do triângulo */}
          {(isFlowCellBlocked ? variants.length > 0 : !showCoverage && variants.length > 0) && (
            <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: shapeW, fontFamily: 'Arial, sans-serif', pointerEvents: 'none' }}>
              {variants.map((v, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap', lineHeight: 1.05 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#94a3b8' : '#6b7280', textTransform: 'uppercase' }}>{v.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827' }}>{v.qty > 0 ? v.qty.toLocaleString('pt-BR') : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center relative"
      style={{ width: '80px', height: '100px', cursor: 'move' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Triângulo SVG */}
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`transition-all duration-200 ${selected ? 'drop-shadow-[0_0_4px_rgba(59,130,246,0.5)]' : ''}`}
      >
        <path
          d="M30 5L55 55H5L30 5Z"
          fill={isDark ? 'none' : 'white'}
          stroke={selected ? '#3b82f6' : (isDark ? '#ffffff' : '#000')}
          strokeWidth="2"
        />
        <text
          x="30"
          y="42"
          textAnchor="middle"
          fontSize="24"
          fontWeight="bold"
          fill={selected ? '#3b82f6' : (isDark ? '#ffffff' : '#000')}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          I
        </text>
      </svg>

      {/* Campo de Quantidade */}
      <div className="mt-2 bg-white border border-gray-300 rounded shadow-sm px-1 flex items-center gap-1">
        <input
          type="number"
          value={inventoryValue}
          onChange={handleChange}
          className="w-12 text-center text-[11px] font-bold outline-none bg-transparent"
          placeholder="Qty"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};
